import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  HStack,
  Badge,
  Text,
  useToast,
  Skeleton,
  IconButton,
  Tooltip,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Input,
  FormControl,
  FormLabel,
  VStack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Card,
  CardBody,
  useColorModeValue,
  Flex,
  Heading,
  Link,
} from "@chakra-ui/react";
import {
  BsCheckCircle,
  BsXCircle,
  BsPlus,
  BsFileEarmarkText,
  BsTrophy,
} from "react-icons/bs";
import vendorPOApi from "../services/vendorPOApi";
import AddVendorPOModal from "./AddVendorPOModal";

const VendorPOProposalList = ({ requestId, requestStatus, onStatusChange }) => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [loading, setLoading] = useState(true);
  const [proposals, setProposals] = useState([]);
  const [stats, setStats] = useState({});
  const [actionLoading, setActionLoading] = useState(null);

  // Reject dialog state
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const cancelRef = React.useRef();

  // Approve confirm dialog
  const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);
  const approveCancelRef = React.useRef();

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  useEffect(() => {
    if (requestId) {
      fetchProposals();
    }
  }, [requestId]);

  const fetchProposals = async () => {
    try {
      setLoading(true);
      const response = await vendorPOApi.getVendorPOProposals(requestId);
      setProposals(response?.data?.proposals || []);
      setStats(response?.data?.stats || {});
    } catch (error) {
      console.error("❌ Error fetching proposals:", error);
      // Don't show error toast if no proposals exist yet
      if (error.response?.status !== 404) {
        toast({
          title: "Error fetching proposals",
          description: error.response?.data?.message || error.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApproveClick = (proposal) => {
    setSelectedProposal(proposal);
    setApproveConfirmOpen(true);
  };

  const handleApproveConfirm = async () => {
    if (!selectedProposal) return;

    try {
      setActionLoading(selectedProposal._id);
      await vendorPOApi.approveVendorPOProposal(
        selectedProposal._id,
        requestId,
      );
      toast({
        title: "Proposal approved",
        description:
          "Vendor selected successfully. All other proposals have been rejected.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setApproveConfirmOpen(false);
      setSelectedProposal(null);
      fetchProposals();
      // Notify parent that status changed to PO_SELECTED
      if (onStatusChange) {
        onStatusChange("PO_SELECTED");
      }
    } catch (error) {
      toast({
        title: "Error approving proposal",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectClick = (proposal) => {
    setSelectedProposal(proposal);
    setRejectionReason("");
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!selectedProposal || !rejectionReason.trim()) {
      toast({
        title: "Rejection reason required",
        status: "warning",
        duration: 2000,
      });
      return;
    }

    try {
      setActionLoading(selectedProposal._id);
      await vendorPOApi.rejectVendorPOProposal(
        selectedProposal._id,
        rejectionReason,
      );
      toast({
        title: "Proposal rejected",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      setRejectDialogOpen(false);
      setSelectedProposal(null);
      setRejectionReason("");
      fetchProposals();
    } catch (error) {
      toast({
        title: "Error rejecting proposal",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddSuccess = () => {
    onClose();
    fetchProposals();
  };

  const formatCurrency = (amount, currency = "INR") => {
    const symbols = { INR: "₹", USD: "$", EUR: "€", GBP: "£" };
    return `${symbols[currency] || "₹"}${amount?.toLocaleString() || 0}`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      SUBMITTED: { color: "blue", label: "Pending" },
      APPROVED: { color: "green", label: "Approved" },
      REJECTED: { color: "red", label: "Rejected" },
    };
    const config = statusConfig[status] || { color: "gray", label: status };
    return <Badge colorScheme={config.color}>{config.label}</Badge>;
  };

  // Check if user can add proposals (allow at any workflow stage, including DRAFT)
  const canAddProposals =
    requestStatus !== "REJECTED" && requestStatus !== "SENT_BACK";
  // Check if user can approve/reject proposals (only when request is APPROVED)
  const canManageProposals =
    requestStatus === "APPROVED" &&
    proposals.some((p) => p.status === "SUBMITTED");

  if (loading) {
    return (
      <Box>
        <Skeleton height="40px" mb={4} />
        <Skeleton height="200px" />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={4}>
        <Heading size="md">Vendor PO Proposals</Heading>
        {canAddProposals && (
          <Button
            leftIcon={<BsPlus />}
            colorScheme="blue"
            size="sm"
            onClick={onOpen}
          >
            Add Vendor Proposal
          </Button>
        )}
      </Flex>

      {/* Statistics */}
      {proposals.length > 0 && (
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={4}>
          <Card
            bg={cardBg}
            borderWidth="1px"
            borderColor={borderColor}
            size="sm"
          >
            <CardBody py={3}>
              <Stat size="sm">
                <StatLabel fontSize="xs">Total Proposals</StatLabel>
                <StatNumber fontSize="lg">{stats.total || 0}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
          <Card
            bg={cardBg}
            borderWidth="1px"
            borderColor={borderColor}
            size="sm"
          >
            <CardBody py={3}>
              <Stat size="sm">
                <StatLabel fontSize="xs">Lowest Quote</StatLabel>
                <StatNumber fontSize="lg" color="green.500">
                  {formatCurrency(stats.lowestQuote)}
                </StatNumber>
              </Stat>
            </CardBody>
          </Card>
          <Card
            bg={cardBg}
            borderWidth="1px"
            borderColor={borderColor}
            size="sm"
          >
            <CardBody py={3}>
              <Stat size="sm">
                <StatLabel fontSize="xs">Highest Quote</StatLabel>
                <StatNumber fontSize="lg" color="red.500">
                  {formatCurrency(stats.highestQuote)}
                </StatNumber>
              </Stat>
            </CardBody>
          </Card>
          <Card
            bg={cardBg}
            borderWidth="1px"
            borderColor={borderColor}
            size="sm"
          >
            <CardBody py={3}>
              <Stat size="sm">
                <StatLabel fontSize="xs">Average Quote</StatLabel>
                <StatNumber fontSize="lg">
                  {formatCurrency(stats.averageQuote)}
                </StatNumber>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>
      )}

      {/* Proposals Table */}
      {proposals.length === 0 ? (
        <Box
          textAlign="center"
          py={8}
          borderWidth="1px"
          borderRadius="md"
          borderColor={borderColor}
        >
          <Text color="gray.500" mb={4}>
            No vendor proposals yet.
          </Text>
          {canAddProposals && (
            <Button leftIcon={<BsPlus />} colorScheme="blue" onClick={onOpen}>
              Add First Vendor Proposal
            </Button>
          )}
          {requestStatus === "REJECTED" || requestStatus === "SENT_BACK" ? (
            <Text fontSize="sm" color="gray.400" mt={2}>
              Vendor proposals cannot be added for {requestStatus} requests.
            </Text>
          ) : null}
        </Box>
      ) : (
        <Box
          borderWidth="1px"
          borderRadius="md"
          borderColor={borderColor}
          overflowX="auto"
        >
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>Vendor</Th>
                <Th>PO Number</Th>
                <Th isNumeric>Quoted Amount</Th>
                <Th>Status</Th>
                <Th>Document</Th>
                <Th>Notes</Th>
                {canManageProposals && <Th>Actions</Th>}
              </Tr>
            </Thead>
            <Tbody>
              {proposals.map((proposal) => (
                <Tr
                  key={proposal._id}
                  bg={proposal.status === "APPROVED" ? "green.50" : undefined}
                >
                  <Td>
                    <HStack>
                      {proposal.status === "APPROVED" && (
                        <BsTrophy color="gold" />
                      )}
                      <Text
                        fontWeight={
                          proposal.status === "APPROVED" ? "bold" : "normal"
                        }
                      >
                        {proposal.vendorId?.name || "Unknown Vendor"}
                      </Text>
                    </HStack>
                  </Td>
                  <Td>{proposal.poNumber}</Td>
                  <Td isNumeric fontWeight="semibold">
                    {formatCurrency(proposal.quotedAmount, proposal.currency)}
                  </Td>
                  <Td>{getStatusBadge(proposal.status)}</Td>
                  <Td>
                    {proposal.poDocumentUrl ? (
                      <Link
                        href={proposal.poDocumentUrl}
                        isExternal
                        color="blue.500"
                      >
                        <HStack>
                          <BsFileEarmarkText />
                          <Text fontSize="sm">View</Text>
                        </HStack>
                      </Link>
                    ) : (
                      <Text color="gray.400" fontSize="sm">
                        N/A
                      </Text>
                    )}
                  </Td>
                  <Td maxW="200px" isTruncated>
                    <Tooltip label={proposal.notes || "No notes"}>
                      <Text fontSize="sm">{proposal.notes || "-"}</Text>
                    </Tooltip>
                  </Td>
                  {canManageProposals && (
                    <Td>
                      {proposal.status === "SUBMITTED" && (
                        <HStack spacing={1}>
                          <Tooltip label="Approve & Select Vendor">
                            <IconButton
                              icon={<BsCheckCircle />}
                              size="sm"
                              colorScheme="green"
                              variant="ghost"
                              onClick={() => handleApproveClick(proposal)}
                              isLoading={actionLoading === proposal._id}
                              aria-label="Approve"
                            />
                          </Tooltip>
                          <Tooltip label="Reject Proposal">
                            <IconButton
                              icon={<BsXCircle />}
                              size="sm"
                              colorScheme="red"
                              variant="ghost"
                              onClick={() => handleRejectClick(proposal)}
                              isLoading={actionLoading === proposal._id}
                              aria-label="Reject"
                            />
                          </Tooltip>
                        </HStack>
                      )}
                    </Td>
                  )}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Request Status Info */}
      {requestStatus === "PO_SELECTED" && (
        <Box
          mt={4}
          p={4}
          bg="green.50"
          borderRadius="md"
          borderWidth="1px"
          borderColor="green.200"
        >
          <HStack>
            <BsCheckCircle color="green" />
            <Text color="green.700" fontWeight="medium">
              Vendor has been selected. This procurement request is complete.
            </Text>
          </HStack>
        </Box>
      )}

      {/* Add Vendor PO Modal */}
      <AddVendorPOModal
        isOpen={isOpen}
        onClose={onClose}
        requestId={requestId}
        onSuccess={handleAddSuccess}
      />

      {/* Approve Confirmation Dialog */}
      <AlertDialog
        isOpen={approveConfirmOpen}
        leastDestructiveRef={approveCancelRef}
        onClose={() => setApproveConfirmOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirm Vendor Selection
            </AlertDialogHeader>

            <AlertDialogBody>
              <VStack align="stretch" spacing={3}>
                <Text>
                  You are about to select{" "}
                  <strong>{selectedProposal?.vendorId?.name}</strong> for this
                  procurement request.
                </Text>
                <Text>
                  Quoted Amount:{" "}
                  <strong>
                    {formatCurrency(
                      selectedProposal?.quotedAmount,
                      selectedProposal?.currency,
                    )}
                  </strong>
                </Text>
                <Text color="orange.600" fontWeight="medium">
                  ⚠️ This action will:
                </Text>
                <Box pl={4}>
                  <Text fontSize="sm">• Approve this vendor's proposal</Text>
                  <Text fontSize="sm">
                    • Auto-reject all other pending proposals
                  </Text>
                  <Text fontSize="sm">
                    • Mark the request as PO_SELECTED (final)
                  </Text>
                </Box>
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={approveCancelRef}
                onClick={() => setApproveConfirmOpen(false)}
              >
                Cancel
              </Button>
              <Button
                colorScheme="green"
                onClick={handleApproveConfirm}
                ml={3}
                isLoading={actionLoading}
              >
                Confirm & Select Vendor
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Reject Dialog */}
      <AlertDialog
        isOpen={rejectDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setRejectDialogOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Reject Proposal
            </AlertDialogHeader>

            <AlertDialogBody>
              <VStack align="stretch" spacing={4}>
                <Text>
                  You are rejecting the proposal from{" "}
                  <strong>{selectedProposal?.vendorId?.name}</strong>.
                </Text>
                <FormControl isRequired>
                  <FormLabel>Rejection Reason</FormLabel>
                  <Input
                    placeholder="e.g., Quote is too high"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                  />
                </FormControl>
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={() => setRejectDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleRejectConfirm}
                ml={3}
                isLoading={actionLoading}
                isDisabled={!rejectionReason.trim()}
              >
                Reject Proposal
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default VendorPOProposalList;
